/**
 * Replace Kaching Bundles native <select> open menus with theme-styled custom dropdowns.
 * Keeps the original <select> in the DOM (hidden) so Kaching change handlers still work.
 */
(function () {
  const SELECTOR = "select.kaching-bundles__bar-variant-select";
  const ENHANCED = "data-meevia-select-enhanced";
  const WRAP_CLASS = "meevia-kb-select";
  let openWrap = null;

  function closeOpen() {
    if (!openWrap) return;
    openWrap.classList.remove("is-open");
    const btn = openWrap.querySelector(".meevia-kb-select__button");
    if (btn) btn.setAttribute("aria-expanded", "false");
    openWrap = null;
  }

  function optionLabel(option) {
    return (option.textContent || option.value || "").replace(/\s+/g, " ").trim();
  }

  function syncButton(wrap, select) {
    const btn = wrap.querySelector(".meevia-kb-select__button-text");
    const selected = select.options[select.selectedIndex];
    if (btn) btn.textContent = selected ? optionLabel(selected) : "";
  }

  function rebuildList(wrap, select) {
    const list = wrap.querySelector(".meevia-kb-select__list");
    if (!list) return;
    list.innerHTML = "";

    Array.from(select.options).forEach((option, index) => {
      const item = document.createElement("button");
      item.type = "button";
      item.className = "meevia-kb-select__option";
      item.setAttribute("role", "option");
      item.dataset.index = String(index);
      item.dataset.value = option.value;
      item.textContent = optionLabel(option);

      // Only trust the native disabled flag — Kaching appends labels like
      // "Unavailable" for context, but the option may still be selectable.
      if (option.disabled) {
        item.classList.add("is-disabled");
        item.setAttribute("aria-disabled", "true");
        item.disabled = true;
      }
      if (option.selected) {
        item.classList.add("is-selected");
        item.setAttribute("aria-selected", "true");
      } else {
        item.setAttribute("aria-selected", "false");
      }

      item.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (item.disabled) return;

        select.selectedIndex = index;
        select.value = option.value;
        select.setAttribute("data-value", option.value);
        select.dispatchEvent(new Event("input", { bubbles: true }));
        select.dispatchEvent(new Event("change", { bubbles: true }));

        list.querySelectorAll(".meevia-kb-select__option").forEach((el) => {
          el.classList.toggle("is-selected", el === item);
          el.setAttribute("aria-selected", el === item ? "true" : "false");
        });
        syncButton(wrap, select);
        closeOpen();
      });

      list.appendChild(item);
    });
  }

  function enhanceSelect(select) {
    if (!select || select.hasAttribute(ENHANCED)) return;
    if (select.previousElementSibling?.classList?.contains(WRAP_CLASS)) {
      select.setAttribute(ENHANCED, "true");
      select.classList.add("meevia-kb-select__native");
      return;
    }

    const parent = select.parentElement;
    if (!parent) return;

    select.setAttribute(ENHANCED, "true");
    select.classList.add("meevia-kb-select__native");

    const wrap = document.createElement("div");
    wrap.className = WRAP_CLASS;

    const button = document.createElement("button");
    button.type = "button";
    button.className = "meevia-kb-select__button";
    button.setAttribute("aria-haspopup", "listbox");
    button.setAttribute("aria-expanded", "false");
    button.setAttribute("aria-label", select.getAttribute("aria-label") || select.dataset.name || "Select option");
    button.innerHTML =
      '<span class="meevia-kb-select__button-text"></span>' +
      '<svg class="meevia-kb-select__chevron" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">' +
      '<path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      "</svg>";

    const list = document.createElement("div");
    list.className = "meevia-kb-select__list";
    list.setAttribute("role", "listbox");

    wrap.appendChild(button);
    wrap.appendChild(list);
    parent.insertBefore(wrap, select);

    syncButton(wrap, select);
    rebuildList(wrap, select);

    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const willOpen = !wrap.classList.contains("is-open");
      closeOpen();
      if (!willOpen) return;
      rebuildList(wrap, select);
      wrap.classList.add("is-open");
      button.setAttribute("aria-expanded", "true");
      openWrap = wrap;
    });

    select.addEventListener("change", () => {
      syncButton(wrap, select);
      rebuildList(wrap, select);
    });

    const optionObserver = new MutationObserver(() => {
      syncButton(wrap, select);
      rebuildList(wrap, select);
    });
    optionObserver.observe(select, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });
  }

  function enhanceAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SELECTOR).forEach(enhanceSelect);
  }

  function syncBadgeLabels(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(".kaching-bundles__bar-label").forEach((el) => {
      const text = (el.textContent || "").replace(/\s+/g, " ").trim();
      if (text === "1 FREE") el.textContent = "GET 1 FREE";
    });
  }

  // Badge already shows "GET 1 FREE" — strip duplicate from title ("Buy 3, Get 1 FREE" → "Buy 3")
  function syncBarTitles(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(".kaching-bundles__bar-title").forEach((el) => {
      const raw = (el.textContent || "").replace(/\s+/g, " ").trim();
      const cleaned = raw
        .replace(/,?\s*get\s*1\s*free/gi, "")
        .replace(/\s{2,}/g, " ")
        .replace(/[,\s]+$/g, "")
        .trim();
      if (cleaned && cleaned !== raw) el.textContent = cleaned;
    });
  }

  function syncKachingCopy(root) {
    syncBadgeLabels(root);
    syncBarTitles(root);
  }

  function init() {
    enhanceAll(document);
    syncKachingCopy(document);

    document.addEventListener("click", (event) => {
      if (openWrap && !openWrap.contains(event.target)) closeOpen();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeOpen();
    });

    const observer = new MutationObserver((mutations) => {
      let shouldScan = false;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (
            node.matches?.(SELECTOR) ||
            node.querySelector?.(SELECTOR) ||
            node.matches?.(".kaching-bundles__bar-label, .kaching-bundles__bar-title") ||
            node.querySelector?.(".kaching-bundles__bar-label, .kaching-bundles__bar-title")
          ) {
            shouldScan = true;
            break;
          }
        }
        if (shouldScan) break;
      }
      if (shouldScan) {
        enhanceAll(document);
        syncKachingCopy(document);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
